import { Box, Stack, TablePagination } from '@mui/material'
import type React from 'react'
import { useEffect, useState } from 'react'
import { Header } from 'src/components/header/Header'
import { HeaderBackButtonMui } from 'src/components/header/HeaderBackButtonMui'
import { HeaderTitle } from 'src/components/header/HeaderTitle'
import Loading from 'src/components/loading/Loading'
import { GlobalNavigation } from 'src/features/globalNavigation/GlobalNavigation'
import ImproveExcelConversionLogList from 'src/features/improveExcelConversionLogs/ImproveExcelConversionLogList'
import type { ImproveExcelConversionLogsProps } from 'src/features/improveExcelConversionLogs/ImproveExcelConversionLogsProps'
import { useListFilter } from 'src/hooks/filter/useListFilter'

const ROWS_PER_PAGE_OPTIONS = [25, 50, 100]
const DEFAULT_ROWS_PER_PAGE = 25

const normalizePagination = (
  limit: number | undefined,
  offset: number | undefined,
): { limit: number; offset: number } => {
  const isValidLimit =
    limit !== undefined && ROWS_PER_PAGE_OPTIONS.includes(limit)
  return {
    limit: isValidLimit ? limit : DEFAULT_ROWS_PER_PAGE,
    offset: isValidLimit ? (offset ?? 0) : 0,
  }
}

const ImproveExcelConversionLogsScene: React.FC<
  ImproveExcelConversionLogsProps
> = (props: ImproveExcelConversionLogsProps) => {
  const [chooseLogFileUUIDs, setChooseLogFileUUIDs] = useState<{
    [key: string]: string
  }>({})
  const [logGetTrigger, setLogGetTrigger] = useState<boolean>(false)
  const { filters, setListFilter } = useListFilter()
  const { limit: rowsPerPage, offset: normalizedOffset } = normalizePagination(
    props.request?.limit,
    props.request?.offset,
  )

  useEffect(() => {
    let limit = DEFAULT_ROWS_PER_PAGE
    let offset = 0

    const storedPagination = filters?.improveExcelConversionLogs?.pagination
    if (storedPagination?.improveUUID === props.improveUUID) {
      const normalized = normalizePagination(
        storedPagination.limit,
        storedPagination.offset,
      )
      limit = normalized.limit
      offset = normalized.offset
    }

    props.updatePagination(props.improveUUID, limit, offset)
    props.getLogs({ improveUUID: props.improveUUID, limit, offset })
    // eslint-disable-next-line react-hooks/set-state-in-effect -- useEffect内でのsetState呼び出しは既存設計のため、リファクタリング時に修正する
    setLogGetTrigger(!logGetTrigger)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    setListFilter({
      ...(filters ?? {}),
      improveExcelConversionLogs: {
        pagination: { ...props.request, improveUUID: props.improveUUID },
        filter: filters?.improveExcelConversionLogs?.filter,
      },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.request?.limit, props.request?.offset])

  // MUI TablePagination は 0 オリジン
  const currentPage = normalizedOffset / rowsPerPage

  const handleChangePage = (
    _: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    // ⚠️ changeLogsPage の第一引数は 1 オリジン
    props.changeLogsPage(
      props.improveUUID,
      newPage + 1,
      rowsPerPage,
      props.filter,
    )
  }

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const size = parseInt(event.target.value, 10)
    props.changeLogsSize(props.improveUUID, size, props.filter)
  }

  return (
    // TODO: サイドメニュー描写の責務を`Routes.tsx`に移植する
    <GlobalNavigation
      resetPagination={() => {
        setListFilter({
          ...(filters ?? {}),
          improveExcelConversionLogs: {
            pagination: {
              improveUUID: props.improveUUID,
              limit: DEFAULT_ROWS_PER_PAGE,
              offset: 0,
            },
            filter: filters?.improveExcelConversionLogs?.filter,
          },
        })
      }}
    >
      <Stack sx={{ height: 'calc(100vh - var(--banner-height, 0px))' }}>
        <Box
          sx={{
            height: 72,
            width: '100%',
            alignItems: 'center',
            boxSizing: 'border-box',
            display: 'block',
            flexDirection: 'row',
          }}
        >
          <Header>
            <HeaderBackButtonMui
              onClick={() => props.goImproveDetail(props.improveUUID)}
            />
            <HeaderTitle
              title={`${props.logs?.[0]?.reportName ?? ''} ダウンロードファイル一覧`}
            />
          </Header>
        </Box>
        <Box
          sx={{
            flexGrow: 1,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {props.isLoading ? (
            <Loading />
          ) : (
            <ImproveExcelConversionLogList
              logs={props.logs}
              getImproveExcelConversionLogFiles={
                props.getImproveExcelConversionLogFiles
              }
              chooseLogUUIDs={chooseLogFileUUIDs}
              setChooseLogUUIDs={setChooseLogFileUUIDs}
            />
          )}
        </Box>
        <TablePagination
          component="div"
          count={props.count}
          page={currentPage}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          labelRowsPerPage="1ページあたり:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}〜${to} (${count !== -1 ? count : '不明'} 件中)`
          }
          sx={{
            flexShrink: 0,
            '& .MuiTablePagination-selectLabel': { margin: 0 },
            '& .MuiTablePagination-displayedRows': { margin: 0 },
          }}
        />
      </Stack>
    </GlobalNavigation>
  )
}

export default ImproveExcelConversionLogsScene
